/**
 * Export helpers for the assistant's charts.
 *
 * The charts are inline SVG we draw ourselves, so everything here works off the
 * live DOM node rather than a charting library's export API.
 *
 * Two details that are easy to get wrong:
 *
 *   - **Serialised SVG must carry its own width/height and xmlns.** On screen
 *     the element inherits its size from CSS and its namespace from the HTML
 *     document. Written to a file or handed to an <img>, it has neither, and
 *     renders at zero size or not at all.
 *   - **Canvas needs a background.** An SVG has a transparent one, so a PNG
 *     pasted into Slack or a document would otherwise show dark text on a
 *     dark theme's background.
 */

/** Clipboard image writing is Chromium-only; Firefox has no ClipboardItem. */
export const canCopyImage =
  typeof window !== 'undefined' &&
  typeof window.ClipboardItem !== 'undefined' &&
  Boolean(navigator.clipboard?.write);

const SCALE = 2; // retina-ish, so a pasted PNG is not soft

function serialise(svg) {
  const clone = svg.cloneNode(true);
  // Size: prefer the rendered box, fall back to the viewBox for a detached node.
  const box = svg.getBoundingClientRect();
  const viewBox = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number);
  const width = Math.round(box.width || viewBox[2] || 760);
  const height = Math.round(box.height || viewBox[3] || 400);

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  if (!clone.getAttribute('viewBox') && viewBox.length === 4) {
    clone.setAttribute('viewBox', viewBox.join(' '));
  }
  return {
    markup: new XMLSerializer().serializeToString(clone),
    width,
    height,
  };
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in Safari; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Rasterise the SVG onto an opaque canvas and resolve a PNG blob. */
function toPngBlob(svg) {
  const { markup, width, height } = serialise(svg);
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * SCALE;
      canvas.height = height * SCALE;
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(blob)
            : reject(new Error('Could not render the image')),
        'image/png',
      );
    };
    image.onerror = () => reject(new Error('Could not render the chart'));
    image.src = source;
  });
}

export async function downloadPng(svg, filename) {
  download(await toPngBlob(svg), `${filename}.png`);
}

export function downloadSvg(svg, filename) {
  const { markup } = serialise(svg);
  download(
    new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }),
    `${filename}.svg`,
  );
}

export async function copyPng(svg) {
  const blob = await toPngBlob(svg);
  await navigator.clipboard.write([
    new window.ClipboardItem({ 'image/png': blob }),
  ]);
}

/**
 * Build CSV from the chart's own rows.
 *
 * Uses the full `visualization.data`, not the truncated on-screen slice — the
 * chart shows a top-N for legibility, but someone copying the data wants all
 * of it. Values stay raw rather than currency-formatted so they paste into a
 * spreadsheet as numbers.
 */
export function toCsv(visualization) {
  const rows = visualization?.data || [];
  if (!rows.length) return '';

  const series = visualization.series || [];
  const headers = [
    visualization.categoryLabel || 'Label',
    ...series.map((s) => s.label || s.key),
  ];

  const escape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    // Quote when the value could otherwise break the row, and double any quotes.
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const lines = [headers.map(escape).join(',')];
  for (const row of rows) {
    lines.push(
      [escape(row.label), ...series.map((s) => escape(row[s.key]))].join(','),
    );
  }
  return lines.join('\r\n'); // CRLF: Excel is happier, everything else copes
}

export function downloadCsv(visualization, filename) {
  const csv = toCsv(visualization);
  if (!csv) throw new Error('There is no data to download');
  // BOM so Excel reads UTF-8 rather than mangling supplier names.
  download(
    new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }),
    `${filename}.csv`,
  );
}

/** A filesystem-safe name derived from the chart title. */
export function filenameFor(visualization) {
  const base = String(visualization?.title || 'juice-chart')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'juice-chart';
}
